import { appAlert, appPromptPassword } from './appDialog';
import { copyTextToClipboard } from './formatListClipboard';
import {
  buildShareDecryptUrl,
  decryptShareBlob,
  encryptShareBlob,
  MAX_SHARE_PARAM_CHARS,
  MAX_SHARE_URL_CHARS,
} from './shareLinkCrypto';
import { serializeSharePlain, sharePlaintextForAdHoc } from './sharePayload';
import {
  AppLocalStorageBackupError,
  exportAppLocalStorageBackup,
  parseAppLocalStorageBackup,
  serializeAppLocalStorageBackup,
  type AppLocalStorageBackup,
} from './appLocalStorageBackup';

export const DEVICE_SETTINGS_SHARE_TITLE = 'Device settings backup';

/** Wrapper JSON copied to clipboard (inner payload is encrypted). */
export const APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_FORMAT = 'dailylife-localStorage-backup-encrypted';
export const APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_VERSION = 1;

export type EncryptedAppLocalStorageBackupEnvelope = {
  format: typeof APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_FORMAT;
  version: number;
  blob: string;
};

export function isEncryptedAppLocalStorageBackupEnvelope(
  value: unknown
): value is EncryptedAppLocalStorageBackupEnvelope {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const o = value as EncryptedAppLocalStorageBackupEnvelope;
  return (
    o.format === APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_FORMAT &&
    typeof o.version === 'number' &&
    o.version <= APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_VERSION &&
    typeof o.blob === 'string' &&
    o.blob.trim().length > 0
  );
}

/** Detect encrypted envelope from pasted/exported text before decrypt. */
export function tryParseEncryptedBackupEnvelope(
  text: string
): EncryptedAppLocalStorageBackupEnvelope | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  return isEncryptedAppLocalStorageBackupEnvelope(parsed) ? parsed : null;
}

/** Encrypt plaintext backup JSON (same crypto as Tools → Encrypt link). */
export async function encryptAppLocalStorageBackupJson(
  plaintextJson: string,
  passphrase: string
): Promise<string> {
  const blob = await encryptShareBlob(plaintextJson, passphrase);
  const envelope: EncryptedAppLocalStorageBackupEnvelope = {
    format: APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_FORMAT,
    version: APP_LOCAL_STORAGE_BACKUP_ENCRYPTED_VERSION,
    blob,
  };
  return JSON.stringify(envelope, null, 2);
}

/** Decrypt envelope and parse into a backup object. */
export async function decryptAndParseAppLocalStorageBackup(
  text: string,
  passphrase: string
): Promise<AppLocalStorageBackup> {
  const envelope = tryParseEncryptedBackupEnvelope(text);
  if (!envelope) {
    throw new AppLocalStorageBackupError(
      'Expected an encrypted device backup (format dailylife-localStorage-backup-encrypted).'
    );
  }
  let plaintext: string;
  try {
    plaintext = await decryptShareBlob(envelope.blob, passphrase);
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Could not decrypt.';
    throw new AppLocalStorageBackupError(msg);
  }
  return parseAppLocalStorageBackup(plaintext);
}

/** Plain JSON backup or encrypted envelope (requires passphrase when encrypted). */
export async function parseAppLocalStorageBackupFromPaste(
  text: string,
  passphrase?: string
): Promise<AppLocalStorageBackup> {
  const envelope = tryParseEncryptedBackupEnvelope(text);
  if (envelope) {
    if (!passphrase?.trim()) {
      throw new AppLocalStorageBackupError(
        'This backup is encrypted. Enter your passphrase to import.'
      );
    }
    return decryptAndParseAppLocalStorageBackup(text, passphrase);
  }
  return parseAppLocalStorageBackup(text);
}

function formatDeviceSettingsLinkError(err: unknown): string {
  if (err instanceof Error && err.message.startsWith('LINK_TOO_LONG:')) {
    const n = parseInt(err.message.slice('LINK_TOO_LONG:'.length), 10);
    const max = MAX_SHARE_PARAM_CHARS.toLocaleString();
    const got = Number.isFinite(n) ? n.toLocaleString() : 'too many';
    return `Encrypted link is ${got} characters (max ${max}). Too much data for one link.`;
  }
  if (err instanceof Error && err.message.startsWith('LINK_URL_TOO_LONG:')) {
    const n = parseInt(err.message.slice('LINK_URL_TOO_LONG:'.length), 10);
    const max = MAX_SHARE_URL_CHARS.toLocaleString();
    const got = Number.isFinite(n) ? n.toLocaleString() : 'too many';
    return `Full link is ${got} characters (max ${max}). Use a shorter passphrase or fewer saved settings.`;
  }
  return err instanceof Error ? err.message : 'Could not create link.';
}

/** Encrypt settings and copy a share URL (?s= only — passphrase not in the link). */
export async function generateAndCopyDeviceSettingsShareLink(): Promise<string | null> {
  const passphrase = await appPromptPassword({
    title: 'Encrypt device settings',
    message:
      'Choose a passphrase. The copied link does not include it — share the passphrase separately.',
    confirmLabel: 'Create link & copy',
    fieldLabel: 'Passphrase',
  });
  if (!passphrase) return null;

  const backup = exportAppLocalStorageBackup();
  const json = serializeAppLocalStorageBackup(backup);
  const plain = sharePlaintextForAdHoc(json, DEVICE_SETTINGS_SHARE_TITLE);

  try {
    const encoded = await encryptShareBlob(serializeSharePlain(plain), passphrase);
    const url = buildShareDecryptUrl(encoded, passphrase, { includeKeyInUrl: false });
    await copyTextToClipboard(url);
    const keyCount = Object.keys(backup.entries).length;
    await appAlert(
      `Encrypted link copied (${keyCount} setting(s)). Open it on the other device, enter the passphrase, then use Import device settings or paste into Config → Import.`,
      'Link copied'
    );
    return url;
  } catch (err) {
    await appAlert(formatDeviceSettingsLinkError(err), 'Create link failed');
    return null;
  }
}

/** True when decrypted share text is a device settings backup JSON. */
export function tryParseDeviceSettingsBackupBody(text: string): AppLocalStorageBackup | null {
  try {
    return parseAppLocalStorageBackup(text);
  } catch {
    return null;
  }
}
